import {
  type Dropdown,
  type LeadsListListData,
  type LeadsOpportunitiesListData,
  type OpportunitiesStatusesListData,
  type PaginatedDropdownList,
  type ProductsListListData,
  type TagsListData,
  type UsersListListData,
  leadsListListInfiniteOptions,
  opportunitiesStatusesListListInfiniteOptions,
  productsListListInfiniteOptions,
  tagsListListInfiniteOptions,
  usersListListInfiniteOptions
} from '@crm/api'
import {
  InfiniteSelect,
  IsTenderSelect,
  RangePickerFilter
} from '@crm/components'
import { useRolePermission } from '@crm/hooks/useRolePermission'
import { UserRoleCRM } from '@crm/types'
import {
  defaultFiltersMultipleValues,
  defaultFiltersNumberValues,
  listTransformResults
} from '@crm/utils'
import { MoreFiltersPopover } from '@shared/components'
import { useQueryUpdate } from '@shared/hooks'
import { useLocales } from '@shared/locales'
import { defaultFilters } from '@shared/utils'

export const OpportunitiesFilter = () => {
  const { t } = useLocales()
  const { params, updateQuery } = useQueryUpdate()

  const hasPermissionMix = useRolePermission([
    UserRoleCRM.manager,
    UserRoleCRM.admin
  ])

  const userValue = defaultFiltersMultipleValues(params?.owner)
  const productValue = defaultFiltersNumberValues(params?.product)
  const leadValue = defaultFiltersMultipleValues(params?.lead, true)
  const statusValue = defaultFiltersMultipleValues(params?.status, true)
  const tagsValue = defaultFiltersMultipleValues(params?.tags, true)

  const filtersCount = [
    'owner',
    'product',
    'lead',
    'status',
    'is_tender',
    'tags'
  ].filter((filter) => params?.[filter])?.length

  const onFilterChange = (
    filter: keyof NonNullable<LeadsOpportunitiesListData['query']>,
    value: string[]
  ) => updateQuery({ [filter]: value, page: defaultFilters.page })

  const onResetMoreFilters = () =>
    updateQuery({
      owner: undefined,
      product: undefined,
      lead: undefined,
      status: undefined,
      tags: undefined
    })

  return (
    <>
      <RangePickerFilter
        fromParam="created_at_gte"
        toParam="created_at_lte"
        placeholder={[
          t('application_date_after'),
          t('application_date_before')
        ]}
      />

      <MoreFiltersPopover
        filterActiveCount={filtersCount}
        onResetMoreFilters={onResetMoreFilters}
      >
        {hasPermissionMix && (
          <InfiniteSelect<UsersListListData, PaginatedDropdownList, Dropdown>
            value={userValue}
            className="select-min-width"
            popupMatchSelectWidth
            placeholder={t('filter.lead_owner')}
            getQueryFunction={usersListListInfiniteOptions}
            onChange={(owner) => onFilterChange('owner', owner)}
            baseOptions={{
              query: {
                preference: params?.owner
              }
            }}
          />
        )}

        <InfiniteSelect<ProductsListListData, PaginatedDropdownList, Dropdown>
          value={productValue}
          className="w-full"
          popupMatchSelectWidth
          placeholder={t('contracts.product')}
          getQueryFunction={productsListListInfiniteOptions}
          onChange={(product) => onFilterChange('product', product)}
          baseOptions={{
            query: {
              preference: params?.product
            }
          }}
        />

        <InfiniteSelect<LeadsListListData, PaginatedDropdownList, Dropdown>
          value={leadValue}
          className="w-full"
          popupMatchSelectWidth
          placeholder={t('filter.leads')}
          getQueryFunction={leadsListListInfiniteOptions}
          onChange={(lead) => onFilterChange('lead', lead)}
          baseOptions={{
            query: {
              preference: params?.lead
            }
          }}
        />

        <InfiniteSelect<
          OpportunitiesStatusesListData,
          PaginatedDropdownList,
          Dropdown
        >
          value={statusValue}
          className="w-full"
          popupMatchSelectWidth
          getQueryFunction={opportunitiesStatusesListListInfiniteOptions}
          onChange={(status) => onFilterChange('status', status)}
          placeholder={t('task.status')}
          baseOptions={{
            query: {
              preference: params?.status
            }
          }}
        />

        <IsTenderSelect />

        <InfiniteSelect<TagsListData, PaginatedDropdownList, Dropdown>
          placeholder={t('sales_funnel_report.tags')}
          getQueryFunction={tagsListListInfiniteOptions}
          getOptions={({ options }) => listTransformResults(options)}
          onChange={(tags) => onFilterChange('tags', tags)}
          baseOptions={{
            query: {
              preference: params?.tags
            }
          }}
          value={tagsValue}
          mode="multiple"
          className="w-full"
          maxTagCount={1}
          maxTagTextLength={6}
        />
      </MoreFiltersPopover>
    </>
  )
}
